import React, { useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import { Mail, RefreshCw, PlusCircle, Wallet } from 'lucide-react';
import { ADD_LAB_COMMISSION_FUND } from '../../../api/laboratory-api';

const RESEND_ACTIVATION = gql`
  mutation ResendActivationEmail($laboratoryId: ID!) {
    resendActivationEmail(laboratoryId: $laboratoryId)
  }
`;

const fmt = (n) =>
  new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD', maximumFractionDigits: 2 }).format(n ?? 0);

const GeneralTab = ({ laboratory, productsCount, promotionsCount, refetch }) => {
  const [sent, setSent] = useState(false);
  const [fundAmount, setFundAmount] = useState('');
  const [addingFund, setAddingFund] = useState(false);

  const fund = laboratory.commissionFund ?? 0;
  const used = laboratory.commissionUsed ?? 0;
  const remaining = fund - used;
  const pct = fund > 0 ? Math.min(100, (used / fund) * 100) : 0;

  const [addFund, { loading: fundLoading }] = useMutation(ADD_LAB_COMMISSION_FUND, {
    onCompleted: () => {
      toast.success('Dotation ajoutée avec succès');
      setFundAmount('');
      setAddingFund(false);
      refetch && refetch();
    },
    onError: (err) => toast.error(err.message || 'Erreur lors de l\'ajout'),
  });

  const [resend, { loading: resending }] = useMutation(RESEND_ACTIVATION, {
    onCompleted: () => {
      setSent(true);
      toast.success('Email d\'activation renvoyé');
      setTimeout(() => setSent(false), 4000);
    },
    onError: (err) => toast.error(err.message || 'Erreur lors du renvoi'),
  });

  const ownerPending = laboratory.owner && !laboratory.owner.isActive;

  return (
    <div className="space-y-4">
      {/* Pending activation banner */}
      {ownerPending && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2 text-amber-800 text-sm">
            <Mail size={16} className="text-amber-500 shrink-0" />
            <span>Le propriétaire <strong>{laboratory.owner.email}</strong> n'a pas encore activé son compte.</span>
          </div>
          <button
            onClick={() => resend({ variables: { laboratoryId: laboratory.id } })}
            disabled={resending || sent}
            className="ml-4 shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60 transition-colors"
          >
            <RefreshCw size={13} className={resending ? 'animate-spin' : ''} />
            {sent ? 'Email envoyé !' : resending ? 'Envoi…' : 'Renvoyer l\'email'}
          </button>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 bg-gray-50">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Informations générales</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Détails du laboratoire</p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Nom du laboratoire</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{laboratory.nom}</dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Propriétaire principal</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {laboratory.owner ? (
                  <div>
                    <p className="font-medium">
                      {laboratory.owner.firstName} {laboratory.owner.lastName}
                    </p>
                    <p className="text-gray-500">{laboratory.owner.email}</p>
                    {laboratory.owner.tel && (
                      <p className="text-gray-500">{laboratory.owner.tel}</p>
                    )}
                    <div className="mt-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Propriétaire
                      </span>
                      {laboratory.owner.isActive ? (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Actif
                        </span>
                      ) : (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          En attente d'activation
                        </span>
                      )}
                    </div>
                    {laboratory.owner.lastLogin && (
                      <p className="text-xs text-gray-400 mt-1">
                        Dernière connexion: {format(new Date(laboratory.owner.lastLogin), 'dd/MM/yyyy HH:mm', { locale: fr })}
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <span className="text-gray-500">Non assigné</span>
                    <p className="text-xs text-gray-400 mt-1">
                      Aucun propriétaire principal n'est défini pour ce laboratoire
                    </p>
                  </div>
                )}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Nombre de collaborateurs</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  {laboratory.staffUsers ? laboratory.staffUsers.length : 0}
                </span>
                {laboratory.staffUsers && laboratory.staffUsers.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {laboratory.staffUsers.slice(0, 3).map((staff, index) => (
                      <p key={index} className="text-xs text-gray-600">
                        • {staff.firstName} {staff.lastName} ({staff.email})
                      </p>
                    ))}
                    {laboratory.staffUsers.length > 3 && (
                      <p className="text-xs text-gray-500">
                        ... et {laboratory.staffUsers.length - 3} autre(s)
                      </p>
                    )}
                  </div>
                )}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Total utilisateurs</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">
                  {laboratory.users ? laboratory.users.length : 0}
                </span>
                <p className="text-xs text-gray-500 mt-1">Propriétaire + collaborateurs</p>
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Nombre de produits</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  {productsCount}
                </span>
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Nombre de promotions</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                  {promotionsCount}
                </span>
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Date de création</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {laboratory.createdAt && format(new Date(laboratory.createdAt), 'dd MMMM yyyy', { locale: fr })}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Dernière mise à jour</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {laboratory.updatedAt && format(new Date(laboratory.updatedAt), 'dd MMMM yyyy, HH:mm', { locale: fr })}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

export default GeneralTab;
